import React, { useEffect, useState } from "react";
import { Col, Form, Row, Button, FormControl } from "react-bootstrap";
import BootstrapSwitchButton from "bootstrap-switch-button-react";
import "./SettingTab.scss";
import {
  useFetchUserStockSettingsQuery,
  useSaveStockExchangeSettingsMutation,
} from "store/services/user.service";
import { DAYS } from "helpers/constants";
import toast from "react-hot-toast";
import Loader from "components/shared/Loader";
import { cloneDeep } from "lodash";
import ErrorComponent from "components/shared/ErrorComponent";

const SettingTab = () => {
  const [daysState, setDaysState] = useState({});
  const [initialDaysState, setInitialDaysState] = useState({});
  const [isSaveButtonVisible, setIsSaveButtonVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  const { data: userStockSettingsData, isLoading, isError } =
    useFetchUserStockSettingsQuery();
  const [saveStockExchangeSettings] = useSaveStockExchangeSettingsMutation();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (!userStockSettingsData?.length) {
      return;
    }
    const initialDaysState = userStockSettingsData?.reduce((acc, exchange) => {
      acc[exchange?._id] = exchange?.user_settings?.stockExchanges?.days;
      return acc;
    }, {});

    setDaysState(initialDaysState);
    setInitialDaysState(initialDaysState);
  }, [userStockSettingsData]);

  const handleToggle = (isChecked, exchangeId, day) => {
    let daysStateClone = cloneDeep(daysState);

    if (!daysStateClone[exchangeId]) {
      daysStateClone[exchangeId] = {
        [day]: isChecked,
      };
    } else {
      daysStateClone[exchangeId][day] = isChecked;
    }

    setDaysState(daysStateClone);
    setIsSaveButtonVisible(true);
  };

  const handleSave = async () => {
    try {
      const dataToSave = Object.entries(daysState)
        .filter(([_, days]) => !!days)
        .map(([exchangeId, days]) => ({
          id: exchangeId,
          days,
        }));

      await saveStockExchangeSettings(dataToSave).unwrap();
      toast.success("Settings updated successfully");
      setIsSaveButtonVisible(false);
      setInitialDaysState(daysState);
    } catch (error) {
      console.log(error);
    }
  };

  const handleCancel = () => {
    setDaysState(initialDaysState);
    setIsSaveButtonVisible(false);
  };

  const handleResetSearch = () => {
    setSearchQuery(""); // Reset the search query
  };

  const filteredStockSettings = userStockSettingsData?.filter((exchange) =>
    exchange?.ticker_name
      ?.toLowerCase()
      .includes(debouncedSearchQuery?.toLowerCase())
  );

  return (
    <>
      <div className="inner settingTab">
        <Row>
          <Col lg="12" className="my-2">
            {isLoading ? (
              <Loader />
            ): isError ? (
              <ErrorComponent />
            ): (
              <Form>
                <div className="d-flex align-items-center mb-3">
                  <FormControl
                    type="text"
                    placeholder="Search stock exchanges"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="rounded-pill"
                    style={{ width: "250px" }}
                  />
                  {searchQuery && (
                    <Button
                      onClick={handleResetSearch}
                      style={{ marginLeft: "10px", width: "100px" }}
                      className="d-inline-flex align-items-center justify-content-center commonBtn"
                    >
                      Reset
                    </Button>
                  )}
                </div>
                <Row>
                  {filteredStockSettings?.length > 0 ? (
                    filteredStockSettings?.map((exchange) => (
                      <Col key={exchange?._id} lg="12" className="my-2 pt-4">
                        <div className="labelInput position-relative radioWrpperMain pt-4 p-2">
                          <label
                            htmlFor=""
                            className="form-label px-2 bg-white themeClr position-absolute fw-sbold"
                          >
                            {exchange?.ticker_name}
                          </label>
                          <ul className="list-unstyled ps-0 mb-0 d-flex align-items-center gap-10 flex-wrap">
                            {Object.keys(DAYS)?.map((day) => {
                              let isChecked =
                                daysState[exchange?._id]?.[day] ?? true;
                              return (
                                <li
                                  key={day}
                                  className="d-flex align-items-center gap-10 p-2 border radioOption"
                                >
                                  <label
                                    htmlFor=""
                                    className="m-0 themeClr fw-sbold"
                                  >
                                    {DAYS[day]}
                                  </label>
                                  <div className="cstmSwitch">
                                    <BootstrapSwitchButton
                                      size="sm"
                                      height={16}
                                      width={30}
                                      checked={isChecked}
                                      onChange={(checked) =>
                                        handleToggle(checked, exchange?._id, day)
                                      }
                                    />
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </Col>
                    ))
                  ) : (
                    <Col lg="12" className="my-2">
                      <div className="no-results text-center">
                        <p>No results found</p>
                      </div>
                    </Col>
                  )}
                </Row>
                {isSaveButtonVisible && (
                  <Col lg="7" className="my-2">
                    <div className="d-flex align-items-center gap-10 btnWrpperGroup">
                      <Button
                        type="button"
                        className="d-inline-flex align-items-center justify-content-center commonBtn cancelBtn w-100"
                        onClick={handleCancel}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        className="d-inline-flex align-items-center justify-content-center commonBtn w-100"
                        onClick={handleSave}
                      >
                        Save
                      </Button>
                    </div>
                  </Col>
                )}
              </Form>
            )}
          </Col>
        </Row>
      </div>
    </>
  );
};

export default SettingTab;
